// src/QuizApp.jsx
import React, { useState, useEffect } from 'react';
import LoginScreen from './components/LoginScreen';
import { QuizCreator } from './components/QuizCreator';
import { Dashboard } from './components/Dashboard';
import { QuizList } from './components/QuizList';
import { QuizHistory } from './components/QuizHistory';
import { QuizStartScreen } from './components/QuizStartScreen';
import { QuizRunner } from './components/QuizRunner';
import { ResultsScreen } from './components/ResultsScreen';
import { getRandomSample, shuffleQuestionOptions } from './utils/quizHelpers';
import * as XLSX from 'xlsx';

const API_BASE = 'https://quizzes.elmejilloncito.com/api';
  
const CORRECT_DELAY = 3000;    // 2 seconds
const INCORRECT_DELAY = 10000; // 10 seconds

export default function QuizApp() {
  // --------- Authentication ---------
  const [username, setUsername] = useState('');
  const [userId, setUserId] = useState(null);
  const [loggedIn, setLoggedIn] = useState(false);

  // --------- Data ---------
  const [quizzes, setQuizzes] = useState([]);
  const [history, setHistory] = useState([]);

  // --------- Quiz Creation ---------
  const [creatingQuizStep, setCreatingQuizStep] = useState(null);
  const [quizName, setQuizName] = useState('');
  const [quizError, setQuizError] = useState('');
  const [allQuestions, setAllQuestions] = useState([]);

  // --------- Quiz Selection & Start ---------
  const [selectedQuiz, setSelectedQuiz] = useState(null);
  const [availableQuestions, setAvailableQuestions] = useState([]);
  const [questionCount, setQuestionCount] = useState(5);
  const [questions, setQuestions] = useState([]);
  const [startQuiz, setStartQuiz] = useState(false);

  // --------- Quiz Runner State ---------
  const [current, setCurrent] = useState(0);
  const [timer, setTimer] = useState(120);
  const [isAnswered, setIsAnswered] = useState(false);
  const [selectedOptions, setSelectedOptions] = useState([]);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [finished, setFinished] = useState(false);

  // --------- Pagination & Filters ---------
  const [quizPage, setQuizPage] = useState(1);
  const quizzesPerPage = 5;
  const [quizFilter, setQuizFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 5;

  // --------- Quiz Editing ---------
  const [editingQuizId, setEditingQuizId] = useState(null);

  // --------- Effects ---------
  useEffect(() => {
    const stored = localStorage.getItem('quiz_user');
    if (stored) {
      const { id, username } = JSON.parse(stored);
      setUserId(id);
      setUsername(username);
      setLoggedIn(true);
      fetchUserQuizzes(id);
      fetchUserHistory(id);
    }
  }, []);

  useEffect(() => {
    if (startQuiz && timer > 0 && !finished) {
      const t = setTimeout(() => setTimer((t) => t - 1), 1000);
      return () => clearTimeout(t);
    }
    if (timer === 0) handleSubmitAnswer();
  }, [timer, startQuiz, current]);

  useEffect(() => {
    if (startQuiz) setTimer(120);
  }, [current, startQuiz]);

  useEffect(() => {
    setSelectedOptions([]);
    setIsAnswered(false);
  }, [current]);

  // --------- Handlers ---------
  async function handleLogin() {
    const res = await fetch(`${API_BASE}/users?username=${username.toLowerCase()}`);
    if (!res.ok) return alert('User not found');
    const data = await res.json();
    localStorage.setItem(
      'quiz_user',
      JSON.stringify({ id: data.id, username: data.username })
    );
    setUserId(data.id);
    setUsername(data.username);
    setLoggedIn(true);
    fetchUserQuizzes(data.id);
    fetchUserHistory(data.id);
  }

  async function fetchUserQuizzes(id) {
    const res = await fetch(`${API_BASE}/quizzes/user/${id}`);
    const data = await res.json();
    setQuizzes(data.sort((a, b) => b.id - a.id));
  }

  async function fetchUserHistory(id) {
    const res = await fetch(`${API_BASE}/attempts/user/${id}`);
    setHistory(await res.json());
  }

  function handleUpload(e) {
    const file = e.target.files[0];
    const reader = new FileReader();
  
    reader.onload = (evt) => {
      const arrayBuffer = evt.target.result;
      // Tell XLSX to parse an ArrayBuffer
      const wb = XLSX.read(arrayBuffer, { type: "array" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      // Use defval:null so we can distinguish empty vs FALSE
      const data = XLSX.utils.sheet_to_json(ws, { defval: null });
  
      const parsed = data.map((row) => ({
        question: row["Question"] || "",
        options: ["Option A", "Option B", "Option C", "Option D", "Option E"].map(
          (col) => {
            const cell = row[col];
            return cell == null ? "" : String(cell).trim();
          }
        ),
        correct: String(row["Correct"] ?? "A").toUpperCase().trim(),
      }));
  
      setAllQuestions(parsed);
    };
  
    // use ArrayBuffer instead of deprecated binary string
    reader.readAsArrayBuffer(file);
  }

  function handleEditQuiz(quiz) {
    setQuizError('');
    setQuizName(quiz.name);
    setAllQuestions(
      quiz.questions?.map(q => ({
        question: q.question,
        options: [q.option_a, q.option_b, q.option_c, q.option_d, q.option_e],
        correct: q.correct
      })) || []
    );
    setEditingQuizId(quiz.id);
  
    // instead of “1” we go to step 0 (name entry)
    setCreatingQuizStep(0);
  }
  

  

  async function finalizeQuizCreation() {
    // validation
    if (!quizName.trim()) {
      setQuizError('Quiz name is required.');
      return;
    }
  
    try {
      let quiz;
  
      if (editingQuizId) {
        // ─── EDIT EXISTING QUIZ ───
  
        // 1) Update the quiz name
        const updateRes = await fetch(`${API_BASE}/quizzes/${editingQuizId}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: quizName }),
        });
        if (updateRes.status === 409) {
          setQuizError('Quiz name already exists.');
          return;
        }
        if (!updateRes.ok) {
          throw new Error('Failed to update quiz.');
        }
  
        // 2) Delete all existing questions for that quiz
        await fetch(`${API_BASE}/questions/quiz/${editingQuizId}`, {
          method: 'DELETE',
        });
  
        // 3) Re-create questions payload, pointing at the same quiz ID
        quiz = { id: editingQuizId, name: quizName };
      } else {
        // ─── CREATE NEW QUIZ ───
  
        const createRes = await fetch(`${API_BASE}/quizzes`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ name: quizName, user_id: userId }),
        });
        if (createRes.status === 409) {
          setQuizError('Quiz name already exists.');
          return;
        }
        if (!createRes.ok) {
          throw new Error('Failed to create quiz.');
        }
        quiz = await createRes.json();
      }
  
      // ─── (Both flows) POST the questions in bulk ───
      const payload = allQuestions.map((q) => ({
        quiz_id: quiz.id,
        question: q.question,
        option_a: q.options[0],
        option_b: q.options[1],
        option_c: q.options[2],
        option_d: q.options[3],
        option_e: q.options[4],
        correct: q.correct,
      }));
  
      const qRes = await fetch(`${API_BASE}/questions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ questions: payload }),
      });
      if (!qRes.ok) {
        throw new Error('Failed to save questions.');
      }
  
      // ─── Update local state ───
      setQuizzes((prev) => {
        const updated = prev.filter((x) => x.id !== quiz.id);
        return [{ ...quiz, questions: allQuestions }, ...updated];
      });
  
      // ─── Reset everything ───
      setEditingQuizId(null);
      setCreatingQuizStep(null);
      setQuizName('');
      setAllQuestions([]);
      setQuizError('');
    } catch (err) {
      console.error(err);
      setQuizError(err.message || 'An unexpected error occurred.');
    }
  }
  

  // inside QuizApp.jsx

  async function startSelectedQuiz(quiz) {
    try {
      const res = await fetch(`${API_BASE}/questions/quiz/${quiz.id}`);

      // 1) Check HTTP status
      if (!res.ok) {
        throw new Error(`Server returned ${res.status} ${res.statusText}`);
      }

      // 2) Try to parse JSON
      let data;
      try {
        data = await res.json();
      } catch (parseErr) {
        // if it's not JSON, pull it as text so we can log it
        const text = await res.text();
        console.error("Invalid JSON from server:", text);
        throw new Error("Failed to parse server response.");
      }

      // 3) All good → set state
      setSelectedQuiz(quiz);
      setAvailableQuestions(data);
      setQuestionCount(5);
    } catch (err) {
      console.error("Could not load quiz questions:", err);
      alert(
        "Sorry, we couldn’t load that quiz’s questions. " +
        "Please try again later."
      );
    }
  }


  function handleStartQuiz() {
    const sampled = getRandomSample(availableQuestions, questionCount);
    setQuestions(sampled.map(shuffleQuestionOptions));
    setStartQuiz(true);
    setCurrent(0);
    setScore(0);
    setAnswers([]);
  }

  function handleSelectOption(idx) {
    setSelectedOptions((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );
  }


function handleSubmitAnswer() {
  if (isAnswered) return;

  // You were missing these two lines:
  const correctIdx = questions[current].correct
    .split(',')
    .map((l) => ({ A: 0, B: 1, C: 2, D: 3, E: 4 }[l.trim()]));

  const correct =
    selectedOptions.length === correctIdx.length &&
    selectedOptions.every((i) => correctIdx.includes(i));

  const newScore = score + (correct ? 1 : 0);
  setScore(newScore);
  setAnswers((a) => [
    ...a,
    {
      question: questions[current].question,
      selected: selectedOptions,
      correct: correctIdx,
    },
  ]);

  setIsAnswered(true);

  const feedbackDelay = correct ? CORRECT_DELAY : INCORRECT_DELAY;

  setTimeout(() => {
    if (current + 1 < questions.length) {
      setCurrent((c) => c + 1);
    } else {
      submitAttempt(newScore);
    }
  }, feedbackDelay);
}

  async function submitAttempt(finalScore = score) {
  await fetch(`${API_BASE}/attempts`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      user_id: userId,
      quiz_id: selectedQuiz.id,
      score: finalScore, // ← Use argument!
      total: questions.length,
    }),
  });
  fetchUserHistory(userId);
  setFinished(true);
  setStartQuiz(false);
}


  function handleRetake() {
    const reshuffled = questions
      .map(shuffleQuestionOptions)
      .sort(() => 0.5 - Math.random());
    setQuestions(reshuffled);
    setFinished(false);
    setCurrent(0);
    setScore(0);
    setAnswers([]);
    setIsAnswered(false);
    setStartQuiz(true);
  }

  function handleBackToDashboard() {
    setFinished(false);
    setSelectedQuiz(null);
  }

  function deleteQuiz(id) {
    if (!window.confirm('Delete quiz and its attempts?')) return;
    fetch(`${API_BASE}/quizzes/${id}`, { method: 'DELETE' }).then((r) => {
      if (r.ok) setQuizzes((q) => q.filter((x) => x.id !== id));
    });
  }

  // --------- Render ---------
  if (!loggedIn) {
    return (
      <LoginScreen
        username={username}
        setUsername={setUsername}
        onLogin={handleLogin}
      />
    );
  }

  // --------- Creator / Editor UI ---------
if (creatingQuizStep !== null) {
  // ── edit-name-only mode ──
  if (editingQuizId) {
    return (
      <div className="max-w-md mx-auto p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4">Edit Quiz Name</h2>
        <input
          type="text"
          value={quizName}
          onChange={(e) => setQuizName(e.target.value)}
          className="w-full border px-3 py-2 rounded mb-2"
          placeholder="Enter new quiz name"
        />
        {quizError && (
          <p className="text-red-500 text-sm mb-2">{quizError}</p>
        )}
        <div className="flex gap-2">
          <button
            onClick={finalizeQuizCreation}
            className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
          >
            Save Name
          </button>
          <button
            onClick={() => {
              setCreatingQuizStep(null);
              setEditingQuizId(null);
              setQuizName('');
              setQuizError('');
            }}
            className="bg-gray-300 text-gray-800 px-4 py-2 rounded hover:bg-gray-400"
          >
            Cancel
          </button>
        </div>
      </div>
    );
  }

  // ── normal create flow ──
  return (
    <QuizCreator
      step={creatingQuizStep}
      quizName={quizName}
      setQuizName={setQuizName}
      onNext={() => setCreatingQuizStep(1)}
      onUpload={handleUpload}
      onFinish={finalizeQuizCreation}
      error={quizError}
    />
  );
}


  if (selectedQuiz && !startQuiz && !finished) {
    return (
      <QuizStartScreen
        selectedQuiz={selectedQuiz}
        availableQuestions={availableQuestions}      // ← new prop
        questionCount={questionCount}
        setQuestionCount={setQuestionCount}
        onStart={handleStartQuiz}
      />
    );
  }

  if (startQuiz) {
    return (
      <QuizRunner
        quizName={selectedQuiz.name}
        questions={questions}
        current={current}
        timer={timer}
        isAnswered={isAnswered}
        selectedOptions={selectedOptions}
        onSelect={handleSelectOption}
        onSubmit={handleSubmitAnswer}
      />
    );
  }

  if (finished) {
    return (
      <ResultsScreen
        username={username}
        quizName={selectedQuiz.name}
        score={score}
        questions={questions}
        answers={answers}
        onRetake={handleRetake}
        onBack={handleBackToDashboard}
      />
    );
  }

  return (
    <Dashboard
      username={username}
      onCreate={() => setCreatingQuizStep(0)}
      onLogout={() => {
        localStorage.removeItem('quiz_user');
        setLoggedIn(false);
      }}
    >
      <QuizList
        quizzes={quizzes}
        quizPage={quizPage}
        quizzesPerPage={quizzesPerPage}
        onTake={startSelectedQuiz}
        onEdit={handleEditQuiz}
        onDelete={deleteQuiz}
        onPageChange={setQuizPage}
      />
      {history.length > 0 && (
        <QuizHistory
          history={history}
          filter={quizFilter}
          onFilter={setQuizFilter}
          page={currentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      )}
    </Dashboard>
  );
}
